import shajs from "sha.js";
import {
  each,
  find,
  isArray,
  isFinite,
  isNumber,
  isObject,
  isString,
  keys,
  mapObject,
  some,
} from "underscore";

export const sanitizeLogRocket = (isProduction) => {
  const isDemo = () => {
    const host = /(?:\w+)/.exec(window?.location?.hostname);
    return host?.[0]?.toLocaleLowerCase() === "demo";
  };
  return !!isProduction && !isDemo();
};

export const hashValue = (value) => {
  const hashedValue = shajs("sha256").update(value).digest("hex");
  return `Anonymized ${hashedValue}`;
};

const anonymousValue = (value) => {
  if (value === null) {
    return "Anonymized null";
  }
  if (isNumber(value)) {
    return isFinite(value) ? "Anonymized finite number" : "Anonymized non-finite number";
  }
  if (isArray(value)) {
    return `Anonymized array of length ${value.length}`;
  }
  if (isObject(value)) {
    return `Anonymized object with ${keys(value)?.length} keys`;
  }
  return `Anonymized ${typeof value}`;
};

const bodyElementsToAnonymize = [
  "AccessToken",
  "access_token",
  "AuthenticationResult",
  "ChallengeParameters",
  "ChallengeResponses",
  "code",
  "confirmPassword",
  "ConfirmationCode",
  "currentPassword",
  "customerName",
  "IdToken",
  "id_token",
  "newPassword",
  "orgTitle",
  "password",
  "Password",
  "RefreshToken",
  "refresh_token",
  "Session",
  "session_state",
  "url",
];

const bodyElementsToHash = [
  "cognitoPoolId",
  "cognitoPoolClientId",
  "documentsBucket",
  "email",
  "orgName",
  "orgFullName",
  "username",
  "Username",
];

// The OIDC token endpoint posts x-www-form-urlencoded, so these fields are handled
// by the form branch.
const tokenRequestElementsToAnonymize = ["client_secret", "code_verifier", "token"];
const elementsToAnonymize = [...tokenRequestElementsToAnonymize, ...bodyElementsToAnonymize];
const FORM_BODY = /^[\w.~+%-]+=[^&]*(?:&[\w.~+%-]+=[^&]*)*$/;

// Logout appends the full id_token as id_token_hint, and the auth callback carries the
// code -- neither ever reaches the body rules below. The callback is a browser navigation
// rather than an intercepted request, so this list has to be applied through LogRocket's
// urlSanitizer too, not the network sanitizer alone.
const urlParamsToAnonymize = [
  "access_token",
  "clientKey",
  "code",
  "confirmationCode",
  "id_token",
  "id_token_hint",
  "session_state",
  "state",
  "token",
];
const urlParamsToHash = ["username", "email"];

const sanitizeUrlParams = (value) => {
  const params = new URLSearchParams(value);
  let sanitized = false;
  each(urlParamsToAnonymize, (param) => {
    if (params.has(param)) {
      params.set(param, anonymousValue(params.get(param)));
      sanitized = true;
    }
  });
  each(urlParamsToHash, (param) => {
    if (params.has(param)) {
      params.set(param, hashValue(params.get(param)));
      sanitized = true;
    }
  });

  return sanitized ? params.toString() : null;
};

export const logRocketUrlSanitizer = (url) => {
  const hashStart = url.indexOf("#");
  const queryStart = url.indexOf("?");
  const hasQuery = queryStart !== -1 && (hashStart === -1 || queryStart < hashStart);
  const queryEnd = hashStart === -1 ? url.length : hashStart;
  const query = hasQuery ? url.slice(queryStart + 1, queryEnd) : null;
  const fragment = hashStart === -1 ? null : url.slice(hashStart + 1);
  const sanitizedQuery = query === null ? null : sanitizeUrlParams(query);
  const sanitizedFragment =
    fragment && FORM_BODY.test(fragment) ? sanitizeUrlParams(fragment) : null;

  if (sanitizedQuery === null && sanitizedFragment === null) {
    return url;
  }

  const prefix = hasQuery ?
    `${url.slice(0, queryStart)}?${sanitizedQuery ?? query}` :
    url.slice(0, queryEnd);
  return hashStart === -1 ? prefix : `${prefix}#${sanitizedFragment ?? fragment}`;
};

const isFormEncoded = (request) => {
  const headers = request.headers ?? {};
  const contentType = find(headers, (_value, name) => name.toLowerCase() === "content-type");

  if (isString(contentType) && contentType) {
    return contentType.toLowerCase().includes("application/x-www-form-urlencoded");
  }

  if (!FORM_BODY.test(request.body)) {
    return false;
  }

  const params = new URLSearchParams(request.body);
  return some([...elementsToAnonymize, ...bodyElementsToHash], (key) => params.has(key));
};

const sanitizeFormBody = (body) => {
  const params = new URLSearchParams(body);
  each(elementsToAnonymize, (elementToSanitize) => {
    if (params.has(elementToSanitize)) {
      params.set(elementToSanitize, anonymousValue(params.get(elementToSanitize)));
    }
  });
  each(bodyElementsToHash, (elementToHash) => {
    if (params.has(elementToHash)) {
      params.set(elementToHash, hashValue(params.get(elementToHash)));
    }
  });
  return params.toString();
};

export const logRocketRequestSanitizer = (request) => {
  if (request.url) {
    request.url = logRocketUrlSanitizer(request.url);
  }
  if (request.headers?.["Authorization"]) {
    request.headers["Authorization"] = `Anonymized ${typeof request.headers["Authorization"]}`;
  }
  if (request.headers?.["authorization"]) {
    request.headers["authorization"] = `Anonymized ${typeof request.headers["authorization"]}`;
  }
  if (request.body) {
    if (isFormEncoded(request)) {
      request.body = sanitizeFormBody(request.body);
      return request;
    }
    try {
      const body = JSON.parse(request.body);
      if (body?.variables) {
        const anonVars = mapObject(body.variables, (val, key) => (key = anonymousValue(val)));
        body.variables = anonVars;
      }
      each(bodyElementsToAnonymize, (elementToSanitize) => {
        if (body?.[elementToSanitize]) {
          body[elementToSanitize] = anonymousValue(body[elementToSanitize]);
        }
      });
      each(bodyElementsToHash, (elementToHash) => {
        if (body?.[elementToHash]) {
          body[elementToHash] = hashValue(body[elementToHash]);
        }
      });
      request.body = JSON.stringify(body);
    } catch (error) {
      // intentionally ignore
    }
  }
  return request;
};

export const logRocketResponseSanitizer = (response) => {
  if (response.body) {
    try {
      const body = JSON.parse(response.body);
      if (body?.data) {
        const dataKeys = keys(body.data);
        each(dataKeys, (key) => {
          const value = body.data[key];
          body.data[key] =
            isArray(value) ?
              value.map((val) => anonymousValue(val)) :
              isObject(value) ?
                mapObject(
                  body.data[key],
                  (val, key) => (key = anonymousValue(val)),
                ) :
                anonymousValue(value);
        });
      }
      each(bodyElementsToAnonymize, (elementToSanitize) => {
        if (body?.[elementToSanitize]) {
          body[elementToSanitize] = anonymousValue(body[elementToSanitize]);
        }
      });
      each(bodyElementsToHash, (elementToHash) => {
        if (body?.[elementToHash]) {
          body[elementToHash] = hashValue(body[elementToHash]);
        }
      });
      response.body = JSON.stringify(body);
    } catch (error) {
      // intentionally ignore
    }
  }
  return response;
};
